import React from "react";
import moment from "moment";
const CostEstimatorDisclaimer: React.FC<{
  planName: string;
  serviceProvider: string;
  supportNumber: string;
}> = ({ planName, serviceProvider, supportNumber }) => {
  const handleLinkPress = (url: string) => {
    window.open(url, "_blank");
  };

  const handleContactNumberPress = (number: string) => {
    window.location.href = `tel:${number}`;
  };

  return (
    <div className="mb-20 overflow-y-auto p-4">
      <div className="flex flex-col gap-1">
        <span className="font-bold">
          Disclaimer:
          <p className="ml-2 inline font-semibold">Transparency in Coverage</p>
        </span>

        <span className="font-bold">
          Plan:
          <p className="ml-2 inline font-semibold">{planName}</p>
        </span>
        <span className="font-semibold">
          Date:
          <p className="ml-2 inline font-normal">
            {" "}
            {moment(new Date()).format("MMMM DD, YYYY")}
          </p>
        </span>

        <p className="my-4">
          This disclaimer notice contains{" "}
          <span className="underline">important information</span> about the
          cost estimate and information on the amount you may ultimately be
          required to pay for this item or service.
        </p>
      </div>

      <div>
        <p className="mb-4 font-bold">I. The Basics</p>
        <p className="mb-4 font-bold underline">
          What should I do with this notice?
        </p>

        <p className="mb-4">
          <span className="text-justify font-bold">Read this notice</span>{" "}
          carefully along with the cost estimate. You may need to request a new
          cost estimate as you obtain new information, such as information on
          additional items or services you will receive as part of your
          treatment.
        </p>
        <p className="mb-4 font-bold underline">What are the key terms?</p>
        <ol type="1">
          <li>
            <p className="text-justify">
              An <span className="font-bold">Allowed Amount</span> or{" "}
              <span className="font-bold">Contracted Amount</span> is the
              maximum amount your health plan will pay for a covered item or
              service furnished by an out-of-network provider.
            </p>
          </li>
          <li>
            <p className="text-justify">
              <span className="font-bold">Your Cost</span> is your share of
              costs for a covered item or service that you must pay (sometimes
              called “out-of-pocket costs” or “cost-sharing”). Some examples of
              cost-sharing are deductibles, coinsurance, and copayments. This
              term does not include other costs you may be responsible for, such
              as premiums, balance billed amounts for out-of-network providers,
              or the cost of services not covered by your health plan.
            </p>
          </li>
          <li>
            <p className="text-justify">
              A <span className="font-bold">Covered Item</span> or{" "}
              <span className="font-bold">Service</span> is an item or service
              that your health plan will pay, either in whole or in part, under
              the terms of your health plan.
            </p>
          </li>
          <li>
            <p className="text-justify">
              An <span className="font-bold">Out-of-Network Provider</span> is a
              provider that does not have a contract with your plan to provide
              services at pre-negotiated rates.
            </p>
          </li>
          <li>
            <p className="text-justify">
              <span className="font-bold">Prerequisites</span> are certain
              requirements your health plan may impose on you or your provider
              so that it can determine whether a health care service, treatment
              plan, prescription drug, or durable medical equipment is medically
              necessary before it will provide benefits for related items and
              services. Prerequisites include prior authorization, concurrent
              review, and step-therapy or fail-first requirements.
            </p>
          </li>
        </ol>
        <p className="my-4">
          Other common medical and insurance terms, including definitions of
          deductibles, coinsurance, and copayments, can be found in the{" "}
          {/* 2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A: this was a <span onClick>
              styled to look like a link — not focusable, no role, so keyboard and screen
              reader users could not open the glossary at all. */}
          <button
            type="button"
            onClick={() =>
              handleLinkPress(
                "https://www.dol.gov/sites/dolgov/files/EBSA/laws-and-regulations/laws/affordable-care-act/for-employers-and-advisers/sbc-uniform-glossary-of-coverage-and-medical-terms-final.pdf",
              )
            }
            className="cursor-pointer text-blue-600 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Uniform Glossary of Coverage and Medical Terms.
          </button>
        </p>
      </div>

      <div>
        <p className="my-4 font-bold">
          II. Important information about your cost estimate
        </p>
        <p className="mb-4 text-justify">
          This estimate is designed to provide you with information about the
          cost of an item or service before you receive care. However, this
          estimate has certain limitations that you should consider before
          making any decision to obtain the item or service.
        </p>

        <ol type="1">
          <li>
            <p className="text-justify">
              If you are treated by an out-of-network provider, after paying the
              cost-sharing amount determined by your health plan, you may still
              receive a bill for the difference between the amount the
              out-of-network provider charges for the item or service and the
              amount paid by your health plan. This is called balance billing,
              and this amount is not included in your cost estimate.
            </p>
          </li>
          <li>
            <p className="text-justify">
              The actual charge for the item or service may be different than
              the cost estimate, depending on the actual care you receive. For
              example, if your physician provides additional services during
              your visit, your charges could be more than the cost estimate.
              This is one reason why it is important to discuss with your
              provider both before and during your visit which items and
              services you will receive and to request a new cost estimate if
              new information becomes available.
            </p>
          </li>
          <li>
            <p className="text-justify">
              This cost estimate is not a benefit determination or guarantee of
              coverage for the item or service for which you requested
              information. For example, your plan may need to determine whether
              the item or service is medically necessary in your case before
              making a payment. You should follow your health plan’s process for
              filing a claim for benefits and contact your health plan to help
              determine if there are any additional requirements that apply to
              you as part of that process.
            </p>
          </li>
          <li>
            <p className="text-justify">
              Certain specified services could be provided without a cost-share
              to you if they are administered as a preventive service.
            </p>
          </li>
          <li>
            <p className="text-justify">
              Cost estimates may also be impacted or changed based on files
              received and imported from your specific network.
            </p>
          </li>
          <li>
            <p className="text-justify">
              Absence of service level accumulator data could affect &apos;your
              estimate&apos;&apos;s accuracy.
            </p>
          </li>
          <li>
            <p className="text-justify">
              Lack of ability to receive near real-time accumulator data could
              impact the member’s actual out-of-pocket cost.
            </p>
          </li>
          <li>
            <p className="text-justify">
              Services that are deemed not medically necessary or not covered
              based on plan-specific exclusions are excluded from this estimate.
            </p>
          </li>
        </ol>

        <p className="mb-4 text-justify">
          For more information on coverage and exclusions, please refer to the
          policy plan&apos;s specific coverage agreement.
        </p>
      </div>

      <div>
        <p className="mb-4 font-bold">III. Important Health Plan Information</p>
        <ol type="1">
          <li>
            <p className="text-justify">
              This plan has exclusions and limitations, meaning that certain
              items and services are not covered. These exclusions and
              limitations can affect the amount of money you may have to pay for
              an item or service, as explained in the terms of your plan.
            </p>
          </li>
          <li>
            <p className="text-justify">
              Some health plans require you to get approval for some items or
              services before you receive them in order for the item or service
              to be covered by your plan. This is called prior authorization.
              Check with your plan to determine whether the item or service you
              are seeking requires prior authorization.
            </p>
          </li>
          <li>
            <p className="text-justify">
              You have the right to appeal certain decisions made by your plan,
              such as a decision to deny payment for a service. Contact your
              plan for more information on your rights to appeal.
            </p>
          </li>
          <li>
            <p className="text-justify">
              Your plan is not required to provide coverage for an item or
              service that is not covered by your plan, even if this estimate
              includes information on costs for an item or service not covered
              by your plan. Be sure to check with your plan to determine whether
              the item or service is covered.
            </p>
          </li>
          <li>
            <p className="text-justify">
              Your cost estimate does not include information on any financial
              assistance programs (such as drug manufacturer&apos;s assistance
              programs) that you may be eligible for. Be sure to check with your
              provider to determine whether any financial assistance programs
              are available to you.
            </p>
          </li>
          <li>
            <p className="text-justify">
              Some plans include out-of-pocket maximums, which are the most you
              have to pay for covered services in a plan year. Your cost
              estimate may be impacted by whether or not you have met your
              out-of-pocket maximum. Check with your plan to determine whether
              you have met your out-of-pocket maximum.
            </p>
          </li>
        </ol>
      </div>

      <div>
        <p className="my-4 font-bold">IV. Prerequisites</p>
        <ol type="1">
          <li>
            <p>
              <span className="font-bold">Prior Authorization:</span> Prior
              Approval or Precertification Your health plan must decide whether
              this item or service is medically necessary before it will cover
              this item or service. This is called either prior authorization,
              preauthorization, prior approval or precertification. Your health
              plan may impose additional costs if you or your provider do not
              submit this item or service for prior authorization,
              preauthorization, prior approval or precertification before the
              item or service is provided.
            </p>
          </li>
          <li>
            <p>
              <span className="font-bold">Concurrent Review:</span> Your health
              plan may require a review during an ongoing course of treatment to
              determine whether the plan will continue to cover the item or
              service. This is called a concurrent review. Your health plan may
              cease covering treatment if you or your provider do not submit
              this item or service for concurrent review within a specified time
              period after beginning treatment.
            </p>
          </li>
          <li>
            <p>
              <span className="font-bold">
                Step-therapy or Fail-first Requirement:
              </span>{" "}
              Your health plan will not pay for higher-cost therapies without
              evidence that certain lower-cost therapies have not been effective
              for the participant, beneficiary, or enrollee (these are known as
              fail-first policies or step-therapy protocols). You may be
              required to try a lower-cost alternative before your plan will
              cover this particular item or service.
            </p>
          </li>
        </ol>
      </div>

      <div>
        <p className="my-4 font-bold">V. What if I need more information?</p>
        <ul className="list-disc pl-8">
          <li>
            <p className="mb-2">
              Call {serviceProvider} at{" "}
              {/* 2.1.1 Keyboard — A / 4.1.2 — A: the support phone number was a
                  <span onClick>, so it could not be dialled without a mouse. */}
              <button
                type="button"
                onClick={() => handleContactNumberPress(supportNumber)}
                className="cursor-pointer text-blue-600 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {supportNumber}
              </button>
            </p>
          </li>
        </ul>
      </div>

      <div>
        <p className="my-4 font-bold">VI. Special Notes</p>
        <ul className="list-disc pl-8">
          <li>
            <p className="mb-2">
              There may be visit limits for this procedure. Please check your
              plan documents.
            </p>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CostEstimatorDisclaimer;
